/**
 * RoleBadge Component
 *
 * Displays a colored badge for user roles (RBAC and legacy).
 * - Super Admin: Red
 * - Company Admin: Orange
 * - Analyst: Blue
 * - Viewer: Gray
 */

import React from 'react';
import { Shield, UserCog, User, Eye, BarChart3 } from 'lucide-react';
import type { UserRole } from '../../services/adminApi';

interface RoleBadgeProps {
  role?: UserRole | string;  // Support both UserRole and RBAC role strings
  displayName?: string;  // Optional display name override
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
  className?: string;
}

export const RoleBadge: React.FC<RoleBadgeProps> = ({
  role,
  displayName,
  size = 'md',
  showIcon = true,
  className = '',
}) => {
  if (!role) {
    role = 'user';
  }

  // Expanded role config to support RBAC roles
  const roleConfig: Record<string, { bg: string; text: string; border: string; label: string; Icon: React.ElementType }> = {
    super_admin: {
      bg: 'bg-red-100',
      text: 'text-red-700',
      border: 'border-red-300',
      label: 'Super Admin',
      Icon: Shield,
    },
    company_admin: {
      bg: 'bg-orange-100',
      text: 'text-orange-700',
      border: 'border-orange-300',
      label: 'Company Admin',
      Icon: UserCog,
    },
    analyst: {
      bg: 'bg-blue-100',
      text: 'text-blue-700',
      border: 'border-blue-300',
      label: 'Analyst',
      Icon: BarChart3,
    },
    viewer: {
      bg: 'bg-gray-100',
      text: 'text-gray-700',
      border: 'border-gray-300',
      label: 'Viewer',
      Icon: Eye,
    },
    user: {
      bg: 'bg-gray-100',
      text: 'text-gray-700',
      border: 'border-gray-300',
      label: 'User',
      Icon: User,
    },
  };

  const sizeConfig = {
    sm: { text: 'text-xs px-2 py-0.5', icon: 'w-3 h-3' },
    md: { text: 'text-sm px-2.5 py-1', icon: 'w-3.5 h-3.5' },
    lg: { text: 'text-base px-3 py-1.5', icon: 'w-4 h-4' },
  };

  const config = roleConfig[role] || roleConfig.user;
  const sizes = sizeConfig[size];
  const label = displayName || config.label;

  return (
    <span
      className={`
        inline-flex items-center gap-1.5 font-medium rounded-full border
        ${config.bg} ${config.text} ${config.border}
        ${sizes.text}
        ${className}
      `}
    >
      {showIcon && <config.Icon className={sizes.icon} />}
      {label}
    </span>
  );
};

export default RoleBadge;
